/**
 * Copyright 2023-2025 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useMemo } from "react";
import { When } from "react-if";
import slugify from "slugify";
import { Text, theme } from "@nordcloud/gnui";
import { KeyValue } from "~/generated/graphql";
import {
  DataDisplayWrapper,
  NoData,
  BaselineDefinedSettings,
  KeyValueList,
} from "~/components";
import {
  getTextFromStringify,
  isEmpty,
  isNotEmpty,
  sortByOrder,
} from "~/tools";
import {
  getBaselineSourceIndex,
  getFlatEventEntities,
  isPatchOrScanEntity,
} from "~/utils";
import {
  getPatchPolicy,
  getVisiblePatchPolicyParams,
  PatchPolicy,
  patchPolicyRules,
} from "~/utils/patchPolicyRules";
import { useEvent } from "~/views/events/EventsDetails/EventProvider";
import {
  EventEntity,
  PlanEntityIncomingEventDetails,
} from "~/views/events/types";
import { SystemActionNames } from "~/views/plans/PlanCreate/components/PlanCreateWizard/constants";
import { paramsExcludedFromBaselinePatching } from "~/views/plans/PlanCreate/components/PlanCreateWizard/PlanSettingsForms/components/constant";

import { separateConnectedWords } from "~/views/plans/utils";

export function AdvancedSettings({
  entity,
}: {
  entity?: EventEntity | PlanEntityIncomingEventDetails;
}) {
  const { event } = useEvent();
  const parameters = entity?.inputParameters?.filter(
    (item) => item.key !== SystemActionNames.DELAY
  );

  const sortedEventEntities = useMemo(() => {
    const batches =
      event?.actionBatches ?? event?.plan?.planActionBatches ?? [];
    const actions = event?.actions ?? event?.plan?.planActions ?? [];

    return sortByOrder([...batches, ...actions]);
  }, [event]);

  const flatEventEntities = useMemo(
    () => getFlatEventEntities(sortedEventEntities),
    [sortedEventEntities]
  );

  const patchPolicy = useMemo(() => getPatchPolicy(parameters), [parameters]);
  const visiblePatchPolicyParams = useMemo(
    () => getVisiblePatchPolicyParams(parameters, patchPolicy),
    [parameters, patchPolicy]
  );

  if (isEmpty(parameters ?? [])) {
    return <NoData hasIcon message="There are no Advanced Action Settings" />;
  }

  const baselineSourceIndex = getBaselineSourceIndex(flatEventEntities);
  const eventActionIndex =
    flatEventEntities.findIndex((item) => item.id === entity?.id) ?? -1;

  const isEntityBaselineDependent =
    baselineSourceIndex !== -1 &&
    baselineSourceIndex < eventActionIndex &&
    isPatchOrScanEntity(entity);

  const displayVersion = () => {
    if (!entity?.action || !("version" in entity.action)) {
      return null;
    }
    return (
      <>
        <Text size="sm" tag="div" color={theme.color.text.text02}>
          Action Version
        </Text>
        <DataDisplayWrapper hasData={entity?.action?.version !== null}>
          <Text tag="div" data-testid="div-action-version">
            {entity.action.version}
          </Text>
          <Text size="sm" color={theme.color.text.text02}>
            {entity.action?.description}
          </Text>
        </DataDisplayWrapper>
      </>
    );
  };

  return (
    <>
      <Text weight="medium">Advanced Action Settings</Text>

      {displayVersion()}

      {parameters?.map((item: KeyValue) => {
        const isDefinedByPatchPolicy =
          patchPolicy &&
          patchPolicyRules[patchPolicy]?.defaultSettings &&
          item.key in patchPolicyRules[patchPolicy].defaultSettings;

        if (isDefinedByPatchPolicy) {
          return null;
        }

        const isHiddenByPatchPolicy =
          patchPolicy &&
          patchPolicyRules[patchPolicy]?.visibleParams &&
          !patchPolicyRules[patchPolicy].visibleParams?.includes(item.key);

        if (isHiddenByPatchPolicy) {
          return null;
        }

        return !isEntityBaselineDependent ||
          paramsExcludedFromBaselinePatching.includes(item.key) ? (
          <div css={{ marginBottom: theme.spacing.spacing03 }} key={item.key}>
            <When condition={isNotEmpty(getTextFromStringify(item.value))}>
              <Text size="sm" tag="div" color={theme.color.text.text02}>
                {separateConnectedWords(item.key)}
              </Text>
              <Text
                data-testid={`text-${slugify(separateConnectedWords(item.key), {
                  lower: true,
                })}`}
              >
                {getTextFromStringify(item.value)}
              </Text>
            </When>
          </div>
        ) : null;
      })}
      <When
        condition={
          patchPolicy &&
          (patchPolicy === PatchPolicy.systemAll ||
            patchPolicy === PatchPolicy.securityOnly) &&
          !isEntityBaselineDependent
        }
      >
        <KeyValueList
          title="Settings defined by Patch Policy"
          items={
            !patchPolicy
              ? []
              : Object.entries(
                  patchPolicyRules[patchPolicy].defaultSettings
                ).map(([key, value]) => ({
                  key,
                  value,
                }))
          }
        />
      </When>
      <BaselineDefinedSettings
        isEntityBaselineDependent={isEntityBaselineDependent}
        parameters={
          visiblePatchPolicyParams.length > 0
            ? visiblePatchPolicyParams
            : parameters ?? []
        }
      />
    </>
  );
}
